import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Filesystem, Directory } from '@capacitor/filesystem';
import TusConstructionImageUploadService from './TusConstructionImageUploadService.js';
import ConstructionImageUploadService from './ConstructionImageUploadService.js';

/**
 * Platform-aware Upload Service
 *
 * This service intelligently routes image uploads based on the platform:
 * - Native iOS/Android: Uses @spoonconsulting/cordova-plugin-background-upload for true background uploads
 * - Web: Uses existing TUS-based resumable uploads
 *
 * Features:
 * - Automatic platform detection
 * - Unified API for all platforms
 * - Background upload support on native platforms
 * - Fallback to web upload methods when needed
 * - Progress tracking and error handling
 * - Queue management integration
 */

class PlatformAwareUploadService {
  constructor() {
    this.platform = null;
    this.isNative = false;
    this.backgroundUploadPlugin = null;
    this.uploadManager = null; // Single manager instance for all uploads
    this.initialized = false;
    this.supportedMimeTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/tiff',
      'image/bmp'
    ];
  }

  /**
   * Initialize the service and detect platform
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Detect platform using Capacitor
      this.platform = Capacitor.getPlatform();
      this.isNative = Capacitor.isNativePlatform();

      console.log('PlatformAware: Detected platform:', this.platform);
      console.log('PlatformAware: Is native platform:', this.isNative);

      // Get device info for additional context
      if (this.isNative) {
        try {
          const deviceInfo = await Device.getInfo();
          console.log('PlatformAware: Device info:', {
            platform: deviceInfo.platform,
            operatingSystem: deviceInfo.operatingSystem,
            osVersion: deviceInfo.osVersion,
            manufacturer: deviceInfo.manufacturer,
            model: deviceInfo.model
          });
        } catch (error) {
          console.warn('PlatformAware: Failed to get device info:', error);
        }
      }

      // Initialize background upload plugin for native platforms
      if (this.isNative) {
        await this.initializeBackgroundUpload();
      }

      this.initialized = true;
      console.log('PlatformAware: Service initialized successfully');
    } catch (error) {
      console.error('PlatformAware: Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Initialize background upload plugin for native platforms
   */
  async initializeBackgroundUpload() {
    try {
      // Check for the correct plugin reference - @spoonconsulting/cordova-plugin-background-upload registers as FileTransferManager
      if (window.cordova && window.FileTransferManager) {
        this.backgroundUploadPlugin = window.FileTransferManager;
        console.log('PlatformAware: Background upload plugin (FileTransferManager) available');

        // Configure plugin defaults
        await this.configureBackgroundUpload();
      } else {
        console.warn('PlatformAware: Background upload plugin not available. Checked for:', {
          cordova: !!window.cordova,
          FileTransferManager: !!window.FileTransferManager,
          plugins: !!window.plugins
        });
        console.warn('PlatformAware: Will fallback to web upload');
      }
    } catch (error) {
      console.warn('PlatformAware: Failed to initialize background upload plugin:', error);
      // Continue without background upload - will use fallback
    }
  }

  /**
   * Configure background upload plugin settings
   */
  async configureBackgroundUpload() {
    if (!this.backgroundUploadPlugin) return;

    try {
      // Initialize a single global FileTransferManager instance
      // This prevents issues with multiple manager instances interfering with each other
      const config = {
        parallelUploadsLimit: 3
      };

      // Create the manager once and store it
      this.uploadManager = this.backgroundUploadPlugin.init(config, (event) => {
        // Global event handler - will be called for all uploads
        console.log('PlatformAware: FileTransferManager global event:', event);

        // Route event to the correct upload based on ID
        if (event && event.id) {
          const uploadInfo = this.activeUploads.get(event.id);
          if (uploadInfo) {
            this.handleFileTransferEvent(event, uploadInfo);
          } else {
            console.warn('PlatformAware: Received event for unknown upload:', event.id);
          }
        }
      });

      // Track active uploads to route events correctly
      this.activeUploads = new Map();

      console.log('PlatformAware: Background upload manager initialized with config:', config);
    } catch (error) {
      console.warn('PlatformAware: Failed to configure background upload:', error);
    }
  }

  /**
   * Upload images using the appropriate method for the current platform
   * @param {Object} uploadData - Upload data with images, construction, etc.
   * @param {Object} callbacks - Progress and status callbacks
   * @returns {Promise<Object>} Upload result
   */
  async uploadImages(uploadData, callbacks = {}) {
    await this.initialize();

    // Validate input
    this.validateUploadData(uploadData);

    console.log('PlatformAware: Starting upload for platform:', this.platform);
    console.log('PlatformAware: Images to upload:', uploadData.images.length);
    console.log('PlatformAware: Will use background upload:', this.shouldUseBackgroundUpload());

    try {
      // Route to appropriate upload method based on platform and availability
      if (this.shouldUseBackgroundUpload()) {
        return await this.uploadWithBackgroundPlugin(uploadData, callbacks);
      } else if (this.isNative) {
        // Native platform but no background upload - use TUS for better resumability
        console.log('PlatformAware: Using TUS upload for native platform (no background upload available)');
        return await this.uploadWithTus(uploadData, callbacks);
      } else {
        // Web platform - use TUS for resumable uploads
        console.log('PlatformAware: Using TUS upload for web platform');
        return await this.uploadWithTus(uploadData, callbacks);
      }
    } catch (error) {
      console.error('PlatformAware: Upload failed:', error);

      // Attempt fallback if primary method fails on native platforms
      if (this.isNative && this.backgroundUploadPlugin && !error.message.includes('fallback')) {
        console.log('PlatformAware: Attempting fallback to TUS upload');
        try {
          return await this.uploadWithTus(uploadData, {
            ...callbacks,
            onError: (err) => {
              err.message = `Fallback upload: ${err.message}`;
              if (callbacks.onError) callbacks.onError(err);
            }
          });
        } catch (fallbackError) {
          console.error('PlatformAware: Fallback upload also failed:', fallbackError);
          throw new Error(`Both primary and fallback uploads failed: ${error.message}`);
        }
      }

      throw error;
    }
  }

  /**
   * Determine if background upload should be used
   *
   * Uses FileTransferManager with /upload/multipart endpoint (simple multipart/form-data)
   * instead of TUS protocol. This enables true background uploads on iOS/Android.
   */
  shouldUseBackgroundUpload() {
    return this.isNative &&
           this.backgroundUploadPlugin &&
           (this.platform === 'ios' || this.platform === 'android');
  }

  /**
   * Upload using background upload plugin for native platforms
   */
  async uploadWithBackgroundPlugin(uploadData, callbacks = {}) {
    if (!this.backgroundUploadPlugin) {
      throw new Error('Background upload plugin not available');
    }

    console.log('PlatformAware: Starting FileTransferManager upload...');

    const { onProgress, onComplete, onError, onImageProgress, onImageComplete } = callbacks;
    const results = {
      uploads: [],
      completed: [],
      failed: [],
      abort: () => this.abortBackgroundUploads(results.uploads)
    };

    try {
      // Use the global manager instance
      const manager = this.uploadManager;

      if (!manager) {
        throw new Error('FileTransferManager not initialized');
      }

      // Get upload endpoint
      const uploadEndpoint = this.getUploadEndpoint();

      // Process each image
      for (let i = 0; i < uploadData.images.length; i++) {
        const image = uploadData.images[i];

        try {
          // Prepare image file for upload
          const imageFile = await this.prepareImageForBackgroundUpload(image, uploadData);
          const filename = this.generateFilename(uploadData, i, image.format);

          // Create temporary file for the plugin
          const tempFilePath = await this.createTempFileForUpload(imageFile, filename);

          const uploadPayload = {
            id: this.generateUploadId(),
            serverUrl: uploadEndpoint,
            filePath: tempFilePath,
            fileKey: 'file',
            fileName: filename,
            mimeType: `image/${image.format || 'jpeg'}`,
            headers: {
              'Authorization': this.getAuthHeader()
              // Don't set Content-Type - FileTransferManager will set it with proper boundary
            },
            parameters: {
              projectId: this.generateProjectId(uploadData.construction),
              imageIndex: i.toString(),
              totalImages: uploadData.images.length.toString(),
              constructionSite: uploadData.construction.label || 'Unknown Site'
            },
            notificationTitle: 'Uploading Construction Images'
          };

          // Add timestamp overlay parameters if requested
          if (uploadData.addDateTime && image.caption) {
            uploadPayload.parameters.addTimestamp = 'true';
            uploadPayload.parameters.timestampText = image.caption;
            uploadPayload.parameters.position = 'bottom-right';
            uploadPayload.parameters.fontSize = '14';
            uploadPayload.parameters.fontColor = 'white';
            uploadPayload.parameters.backgroundColor = 'rgba(0,0,0,0.7)';
            uploadPayload.parameters.padding = '10';
          }

          // Store upload info for event routing
          const uploadInfo = {
            uploadId: uploadPayload.id,
            imageIndex: i,
            manager: manager,
            payload: uploadPayload,
            callbacks: callbacks,
            construction: uploadData.construction // Store construction data for FTP upload
          };

          this.activeUploads.set(uploadPayload.id, uploadInfo);
          results.uploads.push(uploadInfo);

          // Start upload using FileTransferManager
          manager.startUpload(uploadPayload);

          console.log(`PlatformAware: Started FileTransferManager upload for image ${i + 1}: ${uploadPayload.id}`);

        } catch (error) {
          console.error(`PlatformAware: Failed to start background upload for image ${i + 1}:`, error);
          results.failed.push({ imageIndex: i, error });
        }
      }

      // Monitor uploads until completion
      await this.monitorFileTransferUploads(results, callbacks);

      // Cleanup temporary files after all uploads complete
      await this.cleanupTempFiles(results.uploads);

      if (results.failed.length === 0 && onComplete) {
        onComplete({
          projectId: this.generateProjectId(uploadData.construction),
          totalImages: uploadData.images.length,
          completed: results.completed
        });
      }

      return results;

    } catch (error) {
      console.error('PlatformAware: FileTransferManager upload failed:', error);

      // Cleanup temp files even on error
      if (results.uploads && results.uploads.length > 0) {
        await this.cleanupTempFiles(results.uploads).catch(err =>
          console.warn('PlatformAware: Failed to cleanup temp files after error:', err)
        );
      }

      throw new Error(`Background upload failed: ${error.message}`);
    }
  }

  /**
   * Upload a single image using background upload plugin
   */
  async uploadSingleImageWithBackground(image, uploadData, imageIndex, endpoint, callbacks) {
    const { onProgress, onComplete, onError } = callbacks;

    // Prepare image data
    const imageBlob = await this.prepareImageForBackgroundUpload(image, uploadData);
    const filename = this.generateFilename(uploadData, imageIndex, image.format);
    const uploadId = this.generateUploadId();

    // Prepare upload parameters
    const uploadParams = {
      fileKey: 'file',
      fileName: filename,
      mimeType: `image/${image.format || 'jpeg'}`,
      params: {
        projectId: this.generateProjectId(uploadData.construction),
        imageIndex: imageIndex.toString(),
        totalImages: uploadData.images.length.toString(),
        constructionSite: uploadData.construction.label || 'Unknown Site'
      },
      headers: {
        'Authorization': this.getAuthHeader()
        // Don't set Content-Type - FileTransferManager will set it with proper boundary
      }
    };

    // Add timestamp overlay parameters if requested
    if (uploadData.addDateTime && image.caption) {
      uploadParams.params.addTimestamp = 'true';
      uploadParams.params.timestampText = image.caption;
      uploadParams.params.position = 'bottom-right';
      uploadParams.params.fontSize = '14';
      uploadParams.params.fontColor = 'white';
      uploadParams.params.backgroundColor = 'rgba(0,0,0,0.7)';
      uploadParams.params.padding = '10';
    }

    return new Promise((resolve, reject) => {
      const options = {
        ...uploadParams,
        onProgress: (progress) => {
          console.log(`PlatformAware: Background upload progress for ${filename}: ${progress}%`);
          if (onProgress) {
            onProgress({
              uploadId,
              imageIndex,
              bytesUploaded: (progress / 100) * (imageBlob.size || 1024 * 1024),
              bytesTotal: imageBlob.size || 1024 * 1024,
              percentage: progress.toFixed(2)
            });
          }
        },
        onSuccess: (result) => {
          console.log(`PlatformAware: Background upload completed for ${filename}:`, result);
          const uploadResult = {
            uploadId,
            imageIndex,
            uploadUrl: result.url || `${endpoint}/${filename}`,
            downloadUrl: result.downloadUrl || result.url,
            filename,
            projectId: uploadParams.params.projectId
          };

          if (onComplete) onComplete(uploadResult);
          resolve(uploadId);
        },
        onError: (error) => {
          console.error(`PlatformAware: Background upload failed for ${filename}:`, error);
          const errorMessage = this.formatBackgroundUploadError(error);
          if (onError) onError(new Error(errorMessage));
          reject(new Error(errorMessage));
        }
      };

      // Start background upload
      try {
        this.backgroundUploadPlugin.startUpload(
          imageBlob,
          endpoint,
          options,
          (result) => options.onSuccess(result),
          (error) => options.onError(error),
          (progress) => options.onProgress(progress)
        );

        console.log(`PlatformAware: Started background upload for ${filename} with ID: ${uploadId}`);
      } catch (error) {
        reject(new Error(`Failed to start background upload: ${error.message}`));
      }
    });
  }

  /**
   * Upload using TUS service (fallback or web platform)
   */
  async uploadWithTus(uploadData, callbacks = {}) {
    console.log('PlatformAware: Using TUS upload service');
    return await TusConstructionImageUploadService.uploadImages(uploadData, callbacks);
  }

  /**
   * Prepare image for background upload
   */
  async prepareImageForBackgroundUpload(image, uploadData) {
    try {
      // Convert base64 to blob for background upload
      const imageData = image.base64String;

      if (!imageData) {
        throw new Error('No base64 image data provided');
      }

      // Convert base64 to blob
      const byteCharacters = atob(imageData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: `image/${image.format || 'jpeg'}`
      });

      console.log(`PlatformAware: Prepared blob for background upload: ${blob.size} bytes, type: ${blob.type}`);
      return blob;
    } catch (error) {
      console.error('PlatformAware: Failed to prepare image for background upload:', error);
      throw new Error(`Failed to prepare image: ${error.message}`);
    }
  }

  /**
   * Handle FileTransferManager events
   */
  handleFileTransferEvent(event, uploadInfo) {
    console.log('PlatformAware: Handling event for upload:', uploadInfo.uploadId, event);

    const { onImageProgress, onImageComplete, onError } = uploadInfo.callbacks;

    if (event.state === 'UPLOADING' && event.progress !== undefined) {
      // Progress update
      if (onImageProgress) {
        onImageProgress(uploadInfo.imageIndex, {
          percentage: event.progress,
          bytesUploaded: event.bytesUploaded || 0,
          bytesTotal: event.bytesTotal || 1
        });
      }
    } else if (event.state === 'UPLOADED' || event.state === 'SUCCESS') {
      // Upload completed successfully
      const result = {
        uploadId: event.id,
        imageIndex: uploadInfo.imageIndex,
        uploadUrl: event.serverResponse?.url || 'uploaded',
        downloadUrl: event.serverResponse?.downloadUrl || event.serverResponse?.url || 'uploaded',
        filename: uploadInfo.payload.fileName,
        projectId: uploadInfo.payload.parameters.projectId
      };

      console.log(`PlatformAware: Upload completed successfully for ${uploadInfo.payload.fileName}`);

      // Trigger FTP upload to remote server via Parse Cloud function
      (async () => {
        try {
          console.log(`PlatformAware: Calling uploadConstructionImageToFtp for ${uploadInfo.payload.fileName}...`);

          const Parse = window.Parse || (await import('parse')).default;

          // Get the construction object from uploadInfo
          const constructionData = uploadInfo.construction?.value || uploadInfo.construction;

          if (!constructionData) {
            console.warn('PlatformAware: No construction data available for FTP upload');
            return;
          }

          // Prepare construction parameter
          let constructionParam;
          if (constructionData.className || constructionData.id) {
            constructionParam = {
              objectId: constructionData.id || constructionData.objectId,
              className: constructionData.className || 'Baustellen'
            };
            console.log('PlatformAware: Using Parse Object reference:', constructionParam);
          } else {
            constructionParam = constructionData;
            console.log('PlatformAware: Using plain object:', constructionParam);
          }

          console.log('PlatformAware: Calling Parse.Cloud.run with params:', {
            construction: constructionParam,
            filePath: `${result.projectId}/${result.filename}`
          });

          const ftpResult = await Parse.Cloud.run('uploadConstructionImageToFtp', {
            construction: constructionParam,
            filePath: `${result.projectId}/${result.filename}`
          });

          console.log(`PlatformAware: FTP upload successful for ${uploadInfo.payload.fileName}:`, ftpResult);
          result.ftpUpload = ftpResult;
        } catch (ftpError) {
          console.error(`PlatformAware: FTP upload failed for ${uploadInfo.payload.fileName}:`, ftpError);
          result.ftpUploadError = ftpError.message;
          // Don't fail the entire upload if FTP fails - just log it
        }
      })();

      // Mark as completed
      uploadInfo.completed = true;

      // Cleanup from active uploads
      this.activeUploads.delete(uploadInfo.uploadId);

      if (onImageComplete) {
        onImageComplete(uploadInfo.imageIndex, result);
      }

      console.log(`PlatformAware: Upload completed and cleaned up: ${uploadInfo.uploadId}`);
    } else if (event.state === 'FAILED' || event.error) {
      // Upload failed
      const error = new Error(event.error || 'Upload failed');
      uploadInfo.failed = true;
      uploadInfo.error = error;

      // Cleanup from active uploads
      this.activeUploads.delete(uploadInfo.uploadId);

      if (onError) {
        onError(error, uploadInfo.imageIndex);
      }

      console.log(`PlatformAware: Upload failed and cleaned up: ${uploadInfo.uploadId}`);
    }
  }

  /**
   * Monitor FileTransferManager uploads until completion
   */
  async monitorFileTransferUploads(results, callbacks) {
    return new Promise((resolve) => {
      let checkInterval;
      let timeoutId;

      const checkCompletion = () => {
        const totalUploads = results.uploads.length;
        const completedUploads = results.uploads.filter(u => u.completed || u.failed).length;

        console.log(`PlatformAware: Upload progress: ${completedUploads}/${totalUploads}`);

        if (completedUploads >= totalUploads) {
          clearInterval(checkInterval);
          clearTimeout(timeoutId);

          // Populate results arrays for backward compatibility
          results.completed = results.uploads.filter(u => u.completed);
          results.failed = results.uploads.filter(u => u.failed).map(u => ({
            imageIndex: u.imageIndex,
            error: u.error
          }));

          console.log('PlatformAware: All FileTransferManager uploads completed');
          console.log(`PlatformAware: Success: ${results.completed.length}, Failed: ${results.failed.length}`);
          resolve();
        }
      };

      // Check every second for completion
      checkInterval = setInterval(checkCompletion, 1000);

      // Timeout after 10 minutes
      timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        console.warn('PlatformAware: FileTransferManager upload monitoring timed out');

        // Mark remaining uploads as timed out
        results.uploads.forEach(u => {
          if (!u.completed && !u.failed) {
            u.failed = true;
            u.error = new Error('Upload timed out');
          }
        });

        resolve();
      }, 10 * 60 * 1000);

      // Initial check
      checkCompletion();
    });
  }

  /**
   * Create temporary file for FileTransferManager upload
   */
  async createTempFileForUpload(imageBlob, filename) {
    try {
      // Use Capacitor Filesystem to create temporary file
      if (this.isNative) {
        // Debug: Check what's available
        console.log('PlatformAware: Directory object:', Directory);
        console.log('PlatformAware: Directory.Cache:', Directory?.Cache);
        console.log('PlatformAware: Filesystem available:', !!Filesystem);

        // Convert blob to base64
        const base64Data = await this.blobToBase64(imageBlob);

        // Write file to temporary directory
        const result = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Cache || 'CACHE', // Use cache directory for temporary files
          recursive: true
        });

        console.log('PlatformAware: Created temp file:', result.uri);
        return result.uri;
      } else {
        // Fallback: create blob URL for web platform
        const blobUrl = URL.createObjectURL(imageBlob);
        console.log('PlatformAware: Created blob URL:', blobUrl);
        return blobUrl;
      }
    } catch (error) {
      console.error('PlatformAware: Failed to create temp file:', error);
      throw new Error(`Failed to create temporary file: ${error.message}`);
    }
  }

  /**
   * Convert blob to base64
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Clean up temporary files after upload completes
   */
  async cleanupTempFiles(uploads) {
    if (!this.isNative || !uploads || uploads.length === 0) return;

    console.log(`PlatformAware: Cleaning up ${uploads.length} temporary files`);

    for (const upload of uploads) {
      try {
        if (upload.payload && upload.payload.filePath) {
          const filePath = upload.payload.filePath;

          // Only delete files in cache directory (safety check)
          if (filePath.includes('cache') || filePath.includes('Cache')) {
            // Extract just the filename from the full path/URI
            const filename = filePath.split('/').pop();

            await Filesystem.deleteFile({
              path: filename,
              directory: Directory.Cache
            });

            console.log(`PlatformAware: Deleted temp file: ${filename}`);
          }
        }
      } catch (error) {
        // Ignore errors (file might already be deleted)
        console.log(`PlatformAware: Could not delete temp file (may not exist): ${error.message}`);
      }
    }
  }

  /**
   * Abort background uploads
   */
  abortBackgroundUploads(uploads) {
    if (!this.backgroundUploadPlugin) return;

    uploads.forEach(upload => {
      try {
        if (upload.uploadId && upload.manager) {
          // Use FileTransferManager's removeUpload method
          upload.manager.removeUpload(
            upload.uploadId,
            () => console.log(`PlatformAware: Aborted background upload: ${upload.uploadId}`),
            (error) => console.warn(`PlatformAware: Failed to abort upload ${upload.uploadId}:`, error)
          );
        }
      } catch (error) {
        console.warn(`PlatformAware: Failed to abort upload ${upload.uploadId}:`, error);
      }
    });
  }

  /**
   * Validate upload data
   */
  validateUploadData(uploadData) {
    if (!uploadData.images || uploadData.images.length === 0) {
      throw new Error('No images provided for upload');
    }

    if (!uploadData.construction) {
      throw new Error('Construction site is required');
    }

    // Validate each image
    uploadData.images.forEach((image, index) => {
      if (!image.base64String) {
        throw new Error(`Image ${index + 1} has no base64 data`);
      }

      const format = image.format || 'jpeg';
      const mimeType = `image/${format}`;

      if (!this.supportedMimeTypes.includes(mimeType)) {
        throw new Error(`Unsupported image format: ${format}`);
      }

      // Check image size (estimate from base64)
      const sizeInBytes = (image.base64String.length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);
      if (sizeInMB > 10) {
        throw new Error(`Image ${index + 1} too large: ${sizeInMB.toFixed(2)}MB. Maximum 10MB allowed.`);
      }
    });
  }

  /**
   * Get upload endpoint for background uploads
   */
  getUploadEndpoint() {
    // Use multipart endpoint for FileTransferManager background uploads
    const tusServerUrl = import.meta.env.VITE_TUS_SERVER_URL || 'https://app-upload-staging.feess.io/upload';
    // Append /multipart to use the simple multipart endpoint instead of TUS
    return `${tusServerUrl}/multipart`;
  }

  /**
   * Get authorization header for uploads
   */
  getAuthHeader() {
    // Return appropriate authorization header
    // This could be Parse session token or other auth mechanism
    if (window.Parse && window.Parse.User.current()) {
      return `Bearer ${window.Parse.User.current().getSessionToken()}`;
    }
    return '';
  }

  /**
   * Generate unique filename
   */
  generateFilename(uploadData, imageIndex, format) {
    const timestamp = Date.now();
    const userId = window.Parse?.User?.current()?.get('employeeId') || 'user';
    let filename = '';

    if (uploadData.name) {
      filename += `${uploadData.name}_`;
    }
    filename += `${uploadData.date || new Date().toISOString().split('T')[0]}_${timestamp}_${userId}_${imageIndex + 1}.${format || 'jpg'}`;

    return filename;
  }

  /**
   * Generate project ID from construction data
   */
  generateProjectId(construction) {
    const siteId = construction.value?.id || construction.id || 'unknown';
    const siteName = construction.label || construction.name || 'site';

    const cleanName = siteName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);

    const projectId = `${cleanName}`.replace(/--+/g, '-');
    return projectId.length > 100 ? projectId.substring(0, 100) : projectId;
  }

  /**
   * Generate unique upload ID
   */
  generateUploadId() {
    return `platform_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update overall progress across all uploads
   */
  updateOverallProgress(uploads, onProgress) {
    if (!onProgress || uploads.length === 0) return;

    const totalUploads = uploads.length;
    const completedUploads = uploads.filter(u => u.completed).length;
    const overallProgress = (completedUploads / totalUploads) * 100;

    onProgress({
      overallProgress: overallProgress.toFixed(2),
      completedUploads,
      totalUploads,
      activeUploads: totalUploads - completedUploads
    });
  }

  /**
   * Format background upload error messages
   */
  formatBackgroundUploadError(error) {
    if (typeof error === 'string') return error;

    if (error.code) {
      switch (error.code) {
        case 'NETWORK_ERROR':
          return 'Network error during background upload. Please check your connection.';
        case 'FILE_NOT_FOUND':
          return 'Image file could not be found for upload.';
        case 'UPLOAD_CANCELLED':
          return 'Upload was cancelled by the system.';
        case 'UPLOAD_TIMEOUT':
          return 'Upload timed out. Please try again.';
        case 'SERVER_ERROR':
          return 'Server error during upload. Please try again later.';
        default:
          return `Upload failed with code ${error.code}: ${error.message || 'Unknown error'}`;
      }
    }

    return error.message || 'Unknown upload error occurred';
  }

  /**
   * Get platform information
   */
  getPlatformInfo() {
    return {
      platform: this.platform,
      isNative: this.isNative,
      backgroundUploadAvailable: !!this.backgroundUploadPlugin,
      initialized: this.initialized
    };
  }

  /**
   * Test platform capabilities
   */
  async testPlatformCapabilities() {
    await this.initialize();

    const capabilities = {
      platform: this.platform,
      isNative: this.isNative,
      backgroundUpload: !!this.backgroundUploadPlugin,
      tusUpload: true, // Always available
      parseUpload: !!window.Parse
    };

    console.log('PlatformAware: Platform capabilities:', capabilities);
    return capabilities;
  }
}

// Export singleton instance
export default new PlatformAwareUploadService();
